import { useEffect, useMemo, useState } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import {
  DEFAULT_LONG_PRICING_CONFIG,
  DEFAULT_PRICING_CONFIG,
  generatePricingPoints,
  toMajorUnit,
  toMinorUnit,
} from "../pricing-configurator.const";
import { ParkingRateFormValues } from "../parking-payment.const";
import {
  AnchorUnitEnum,
  GeneratedPricing,
  GenerationModeEnum,
  PricingAnchor,
  RateTypeEnum,
  RoundModeEnum,
} from "../pricing-configurator.types";

type PricingConfiguratorOptions = {
  control: Control<ParkingRateFormValues>;
  setValue: UseFormSetValue<ParkingRateFormValues>;
  rateType: RateTypeEnum;
};

export const usePricingConfigurator = ({ control, setValue, rateType }: PricingConfiguratorOptions) => {
  // Single source of truth: the form. No duplicate local state.
  const config = useWatch({ control, name: "pricingConfiguration" }) ?? {
    generationMode: { mode: DEFAULT_PRICING_CONFIG.generationMode },
    roundingRules: {
      roundTo: DEFAULT_PRICING_CONFIG.roundTo,
      roundMode: DEFAULT_PRICING_CONFIG.roundMode,
      keepAnchorsExact: DEFAULT_PRICING_CONFIG.keepAnchorsExact,
    },
    anchors: [],
  };

  // Anchors are exposed in major units (euros) for UI; the form stores minor units (centimes).
  const anchors = useMemo<PricingAnchor[]>(
    () => (config.anchors ?? []).map((a) => ({ ...a, priceAmount: toMajorUnit(a.priceAmount) })),
    [config.anchors],
  );

  const generationMode = config.generationMode?.mode ?? DEFAULT_PRICING_CONFIG.generationMode;
  const roundTo = config.roundingRules?.roundTo ?? DEFAULT_PRICING_CONFIG.roundTo;
  const roundMode = config.roundingRules?.roundMode ?? DEFAULT_PRICING_CONFIG.roundMode;
  const keepAnchorsExact = config.roundingRules?.keepAnchorsExact ?? DEFAULT_PRICING_CONFIG.keepAnchorsExact;

  // Local state for the inactive step value — survives mode switches within a mounted session,
  // but resets to default if the tab is unmounted and remounted.
  const [timeStep, setTimeStepState] = useState<number>(() =>
    config.generationMode?.mode === GenerationModeEnum.TIME_STEP
      ? (config.generationMode.stepValue ?? DEFAULT_PRICING_CONFIG.timeStep)
      : DEFAULT_PRICING_CONFIG.timeStep,
  );
  const [priceStep, setPriceStepState] = useState<number>(() =>
    config.generationMode?.mode === GenerationModeEnum.PRICE_STEP
      ? (config.generationMode.stepValue ?? DEFAULT_PRICING_CONFIG.priceStep)
      : DEFAULT_PRICING_CONFIG.priceStep,
  );

  // Keep the shortcuts form field in sync whenever anchor shortcut flags change.
  useEffect(() => {
    const emittedKeys = new Set<string>();
    const shortcuts = (config.anchors ?? [])
      .filter((a) => a.isShortcut && (rateType === RateTypeEnum.SHORT || a.unit))
      .filter((a) => {
        const key = `${a.duration}-${a.unit}`;
        if (emittedKeys.has(key)) return false;
        emittedKeys.add(key);
        return true;
      })
      .map((a) => ({
        duration: a.duration,
        unit: rateType === RateTypeEnum.SHORT ? AnchorUnitEnum.MIN : a.unit!,
      }));
    setValue("shortcuts", shortcuts);
  }, [config.anchors, rateType, setValue]);

  // Converts major-unit anchors to minor units before writing to the form.
  const writeAnchors = (newAnchors: PricingAnchor[]) => {
    setValue("pricingConfiguration", {
      ...config,
      anchors: newAnchors.map((a) => ({ ...a, priceAmount: toMinorUnit(a.priceAmount) })),
    });
  };

  const setAnchors = (newAnchors: PricingAnchor[]) => writeAnchors(newAnchors);

  const setGenerationMode = (mode: GenerationModeEnum) => {
    const stepValue =
      mode === GenerationModeEnum.TIME_STEP ? timeStep : mode === GenerationModeEnum.PRICE_STEP ? priceStep : undefined;
    setValue("pricingConfiguration", { ...config, generationMode: { mode, stepValue } });
  };

  const setTimeStep = (value: number) => {
    setTimeStepState(value);
    if (generationMode === GenerationModeEnum.TIME_STEP) {
      setValue("pricingConfiguration", {
        ...config,
        generationMode: { ...config.generationMode, stepValue: value },
      });
    }
  };

  const setPriceStep = (value: number) => {
    setPriceStepState(value);
    if (generationMode === GenerationModeEnum.PRICE_STEP) {
      setValue("pricingConfiguration", {
        ...config,
        generationMode: { ...config.generationMode, stepValue: value },
      });
    }
  };

  const setRoundTo = (value: number) => {
    setValue("pricingConfiguration", {
      ...config,
      roundingRules: { ...config.roundingRules, roundTo: value },
    });
  };

  const setRoundMode = (value: RoundModeEnum) => {
    setValue("pricingConfiguration", {
      ...config,
      roundingRules: { ...config.roundingRules, roundMode: value },
    });
  };

  const setKeepAnchorsExact = (value: boolean) => {
    setValue("pricingConfiguration", {
      ...config,
      roundingRules: { ...config.roundingRules, keepAnchorsExact: value },
    });
  };

  const removeAnchor = (index: number) => {
    if (anchors.length > 1) {
      writeAnchors(anchors.filter((_, i) => i !== index));
    }
  };

  const updateAnchor = (index: number, field: keyof PricingAnchor, value: number) => {
    const newAnchors = [...anchors];
    newAnchors[index] = { ...newAnchors[index], [field]: value };
    writeAnchors(newAnchors);
  };

  const updateAnchorUnit = (index: number, unit: AnchorUnitEnum) => {
    const newAnchors = [...anchors];
    newAnchors[index] = { ...newAnchors[index], unit };
    writeAnchors(newAnchors);
  };

  const insertAnchorAfter = (index: number) => {
    const current = anchors[index];
    const next = anchors[index + 1];
    const midDuration = next ? Math.round((current.duration + next.duration) / 2) : current.duration + 30;
    const midPriceAmount = next ? (current.priceAmount + next.priceAmount) / 2 : current.priceAmount + 5;

    const newAnchors = [...anchors];
    newAnchors.splice(index + 1, 0, { id: crypto.randomUUID(), duration: midDuration, priceAmount: midPriceAmount });
    writeAnchors(newAnchors);
  };

  const toggleShortcut = (index: number) => {
    const newAnchors = [...anchors];
    newAnchors[index] = { ...newAnchors[index], isShortcut: !newAnchors[index].isShortcut };
    writeAnchors(newAnchors);
  };

  const reorderAnchors = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const newAnchors = [...anchors];
    const [removed] = newAnchors.splice(fromIndex, 1);
    newAnchors.splice(toIndex, 0, removed);
    writeAnchors(newAnchors);
  };

  // Resets anchors and generation mode to defaults in a single write to avoid stale-closure issues.
  const resetToDefault = () => {
    const defaults = rateType === RateTypeEnum.LONG ? DEFAULT_LONG_PRICING_CONFIG : DEFAULT_PRICING_CONFIG;
    setTimeStepState(defaults.timeStep);
    setPriceStepState(defaults.priceStep);
    setValue("pricingConfiguration", {
      ...config,
      anchors: defaults.anchors.map((a) => ({
        ...a,
        id: crypto.randomUUID(),
        priceAmount: toMinorUnit(a.priceAmount),
      })),
      generationMode: { mode: defaults.generationMode, stepValue: defaults.timeStep },
    });
  };

  const generatedPricing = useMemo<GeneratedPricing>(() => {
    const stepValue =
      generationMode === GenerationModeEnum.TIME_STEP
        ? timeStep
        : generationMode === GenerationModeEnum.PRICE_STEP
          ? priceStep
          : undefined;
    return generatePricingPoints(anchors, generationMode, stepValue, { roundTo, roundMode, keepAnchorsExact });
  }, [anchors, generationMode, timeStep, priceStep, roundTo, roundMode, keepAnchorsExact]);

  return {
    anchors,
    generationMode,
    timeStep,
    priceStep,
    roundTo,
    roundMode,
    keepAnchorsExact,
    generatedPricing,
    setAnchors,
    setGenerationMode,
    setTimeStep,
    setPriceStep,
    setRoundTo,
    setRoundMode,
    setKeepAnchorsExact,
    removeAnchor,
    updateAnchor,
    updateAnchorUnit,
    toggleShortcut,
    insertAnchorAfter,
    reorderAnchors,
    resetToDefault,
  };
};
